import { useState, useEffect, useMemo } from 'react';
import axiosVortex from '../../../utils/axios.instance';

// Define the structure of the order
interface Order {
  token: number;
  exchange: string;
  transaction_type: 'SELL' | 'BUY';
  product: string;
  variety: string;
  quantity: number;
  price: number;
}

// Define the expected response structure
interface MarginResponse {
  initial_margin: number;
  required_margin: number;
  status: string;
}

interface UseMarginsBasketResult {
  data: MarginResponse | null;
  loading: boolean;
  error: string | null;
}

const useMarginsBasket = (orders: Order[], shouldFetch: boolean): UseMarginsBasketResult => {
  const [data, setData] = useState<MarginResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Properly memoize orders by using deep comparison in the dependency array
  const stableOrders = useMemo(() => orders, [
    // Use individual properties of each order as dependencies instead of JSON.stringify
    orders.length,
    ...orders.flatMap(order => [
      order.token,
      order.exchange,
      order.transaction_type,
      order.product,
      order.variety,
      order.quantity,
      order.price
    ])
  ]);

  useEffect(() => {
    // Only fetch data if shouldFetch is true and we have orders
    if (!shouldFetch || stableOrders.length === 0) {
      return; // Exit early if shouldFetch is false or no orders
    }

    let isMounted = true; // Flag to prevent state updates after unmount

    const fetchMarginsBasket = async () => {
      setLoading(true);
      setError(null); // Reset error before making the request

      const payload = { orders: stableOrders };

      try {
        // Make the POST request using axios
        const response = await axiosVortex.post('margins/basket', payload);

        // Only update state if component is still mounted
        if (isMounted) {
          setData(response.data);
        }
      } catch (error: any) {
        // Only update state if component is still mounted
        if (isMounted) {
          setError(error?.response?.data?.message || error?.message || 'An unknown error occurred');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchMarginsBasket();

    // Cleanup function to prevent updates after unmount
    return () => {
      isMounted = false;
    };
  }, [stableOrders, shouldFetch]); // Dependencies properly handled now

  return { data, loading, error };
};

export default useMarginsBasket;